/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 *
 */

/**
 * @module bulb-attached-file/bulbattachedfileediting
 */

import { Plugin, Widget, toWidget } from 'ckeditor5';
import type {
  DowncastConversionApi, UpcastConversionApi, ViewElement, ModelElement,
} from 'ckeditor5';

import { InsertBulbAttachedFileCommand } from './bulbattachedfilecommand';
import { formatBytes, getBlockVersion } from '../utils';

/**
 * The bulb attached file editing, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbAttachedFileEditing extends Plugin {

  /**
   * @inheritDoc
   *
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbAttachedFileEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('insertAttachedFile', new InsertBulbAttachedFileCommand(this.editor));
  }

  /**
   * Method that defines the schema four our attached file block.
   *
   * @private
   */
  private defineSchema(): void {
    const { schema } = this.editor.model;

    schema.register('attachedFile', {
      isObject: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id', 'blockAlignment', 'width', 'bulb-block-version'],
    });
  }

  /**
   * Method that creates and initializes the downcast ( editing and data ) and
   * upcast converters for our attached file block.
   *
   * @private
   */
  private defineConverters(): void {
    const { conversion } = this.editor;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'attached-file',
      },
      model: (
        viewElement: ViewElement,
        { writer: modelWriter }: UpcastConversionApi,
      ) => modelWriter.createElement('attachedFile', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, viewElement),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'attachedFile',
      view: (
        modelElement: ModelElement,
        { writer: viewWriter }: DowncastConversionApi,
      ) => viewWriter.createEmptyElement('figure', {
        class: 'attached-file',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'attachedFile',
      view: (modelElement, { writer: viewWriter }: DowncastConversionApi) => {
        const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as string];

        if (contentBlock) {
          const attachedFile = contentBlock.attachedFileAsset;
          const { url } = attachedFile;
          const fileSize = viewWriter.createText(formatBytes(contentBlock.fileSize));
          const fileName = viewWriter.createText(contentBlock.fileName);

          const figure = viewWriter.createContainerElement('figure', {
            class: 'attached-file no-editor-placeholder',
            'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
            'bulb-block-version': modelElement.getAttribute('bulb-block-version') || 0,
          });

          const attachedFileWrapper = viewWriter.createContainerElement('div', {
            class: 'attached-file-wrapper',
          });

          const icon = viewWriter.createEmptyElement('i', { class: 'attached-file-icon fas fa-paperclip' });
          const fileNameLink = viewWriter.createContainerElement('a', { class: 'attached-file-name', href: url });
          const description = viewWriter.createContainerElement('div', { class: 'attached-file-size' });

          viewWriter.insert(viewWriter.createPositionAt(fileNameLink, 0), fileName);
          viewWriter.insert(viewWriter.createPositionAt(description, 0), fileSize);

          // TODO MCL: Fix this so the user can edit it if needed, as of now, the title is not editable.
          // fileNameLink = toWidgetEditable(fileNameLink, viewWriter);

          viewWriter.insert(viewWriter.createPositionAt(attachedFileWrapper, 'end'), icon);
          viewWriter.insert(viewWriter.createPositionAt(attachedFileWrapper, 'end'), fileNameLink);
          viewWriter.insert(viewWriter.createPositionAt(attachedFileWrapper, 'end'), description);
          viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), attachedFileWrapper);

          viewWriter.setCustomProperty('file', true, figure);
          return toWidget(figure, viewWriter, { label: 'Attached File Block', hasSelectionHandle: true });
        }
      },
    });
  }
}
